import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  Sparkles,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  CheckCheck,
  XCircle,
  Clock,
  ShieldAlert,
  Link2,
  Tag as TagIcon,
  Wand2,
} from 'lucide-react';

const API = '/api';

const ACTION_META = {
  keep_live: { label: 'Live laten', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  update_copy: { label: 'Tekst updaten', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  update_price: { label: 'Prijs updaten', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  update_url: { label: 'URL updaten', cls: 'bg-violet-100 text-violet-800 border-violet-200' },
  needs_review: { label: 'Review nodig', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  hide_temporarily: { label: 'Tijdelijk verbergen', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  replace_with_better_option: { label: 'Vervangen door beter', cls: 'bg-pink-100 text-pink-800 border-pink-200' },
  reject: { label: 'Afwijzen', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  later: { label: 'Later', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const DIFF_FIELD_LABELS = {
  product_url: 'Product-URL',
  price_indication: 'Prijs',
  reason_short: 'Beschrijving',
  audience: 'Doelgroep',
  compat_note: 'Compat-notitie',
  safety_note: 'Veiligheidsnotitie',
  tags: 'Tags',
};

function DiffRow({ field, current, proposed }) {
  const label = DIFF_FIELD_LABELS[field] || field;
  const render = (val) => {
    if (val === null || val === undefined || val === '') return <span className="text-muted-foreground italic">leeg</span>;
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'string' && val.startsWith('http')) {
      return (
        <a href={val} target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-1 break-all">
          {val} <ExternalLink className="w-3 h-3" />
        </a>
      );
    }
    return <span>{String(val)}</span>;
  };
  return (
    <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 text-[11px] py-1.5 border-b border-border/40 last:border-0">
      <div className="font-medium text-muted-foreground">{label}</div>
      <div>
        <div className="text-rose-800 bg-rose-50 rounded px-2 py-1 line-through break-words">{render(current)}</div>
        <div className="text-emerald-900 bg-emerald-50 rounded px-2 py-1 mt-1 break-words">{render(proposed)}</div>
      </div>
    </div>
  );
}

export default function ProposalPanel({ product, token, onApplied, onRejected }) {
  const [expanded, setExpanded] = useState(false);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const proposal = product?.proposed_update || null;
  const diff = proposal?.diff_computed || proposal?.diff || null;
  const proposalStatus = proposal?.proposal_status;

  const runAgentCheck = async () => {
    setChecking(true);
    try {
      const res = await axios.post(
        `${API}/admin/energy-control/products/agent-check/${product.product_id}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.proposal) {
        toast.success(`Agent voorstel gereed · ${res.data.proposal.recommended_action || 'needs_review'}`);
        setExpanded(true);
        onApplied?.(null); // trigger refresh
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Agent-check mislukt: ${detail}`);
    } finally {
      setChecking(false);
    }
  };

  const applyScope = async (scope) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/energy-control/products/apply-proposal/${product.product_id}`,
        { scope },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const coerced = res.data?.coerced_fields || [];
      coerced.forEach((c) => toast.warning(`URL geweigerd: ${c.field}`, { description: c.submitted }));
      toast.success(`Voorstel toegepast (${scope})`);
      onApplied?.(res.data?.product);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Toepassen mislukt: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const rejectAs = async (markAs) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/energy-control/products/reject-proposal/${product.product_id}`,
        { mark_as: markAs },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(markAs === 'later' ? 'Voorstel op later gezet' : 'Voorstel afgewezen');
      onRejected?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Afwijzen mislukt: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  if (!proposal) {
    return (
      <button
        type="button"
        disabled={checking}
        onClick={runAgentCheck}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-dashed border-indigo-300 text-indigo-700 bg-indigo-50/60 hover:bg-indigo-100 disabled:opacity-50"
        data-testid={`ec-agent-check-btn-${product.product_id}`}
      >
        {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        {checking ? 'Controleren…' : 'Agent productcontrole'}
      </button>
    );
  }

  const action = proposal.recommended_action || 'needs_review';
  const actionMeta = ACTION_META[action] || ACTION_META.needs_review;
  const hasChanges = diff && Object.keys(diff).length > 0;

  return (
    <div
      className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/30"
      data-testid={`ec-proposal-panel-${product.product_id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-indigo-50 rounded-lg transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
        <span className="text-xs font-semibold text-indigo-900">Agent-voorstel</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${actionMeta.cls}`}>
          {actionMeta.label}
        </span>
        {proposalStatus === 'applied' && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
            Toegepast
          </span>
        )}
        {proposalStatus === 'rejected' && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200">
            Afgewezen
          </span>
        )}
        {proposalStatus === 'later' && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
            Later
          </span>
        )}
        {hasChanges && (
          <span className="text-[10px] text-indigo-700">{Object.keys(diff).length} wijziging(en)</span>
        )}
        {proposal.availability_status && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ml-auto ${
            proposal.availability_status === 'available' ? 'bg-emerald-100 text-emerald-800' :
            proposal.availability_status === 'unavailable' ? 'bg-rose-100 text-rose-800' :
            proposal.availability_status === 'changed' ? 'bg-amber-100 text-amber-800' :
            'bg-slate-100 text-slate-700'
          }`}>
            URL {proposal.availability_status}
          </span>
        )}
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 text-[11px]">
          {proposal.review_summary && (
            <div className="rounded bg-white border border-border p-2">
              <div className="font-semibold text-muted-foreground uppercase tracking-wide text-[10px] mb-1">Review-samenvatting</div>
              <p className="text-foreground">{proposal.review_summary}</p>
            </div>
          )}
          {proposal.review_risks?.length > 0 && (
            <div className="rounded bg-amber-50 border border-amber-200 p-2">
              <div className="font-semibold text-amber-900 uppercase tracking-wide text-[10px] mb-1">Review-risico's</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {proposal.review_risks.map((r, i) => <li key={i} className="text-amber-900">{r}</li>)}
              </ul>
            </div>
          )}
          {proposal.agent_note && (
            <div className="rounded bg-slate-50 border border-slate-200 p-2 text-slate-700">
              <span className="font-semibold uppercase tracking-wide text-[10px]">Agent-notitie:</span> {proposal.agent_note}
            </div>
          )}
          {proposal.source_urls?.length > 0 && (
            <div>
              <div className="font-semibold text-muted-foreground uppercase tracking-wide text-[10px] mb-1 flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Bronnen ({proposal.source_urls.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {proposal.source_urls.map((u) => (
                  <a
                    key={u}
                    href={u}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-border text-primary hover:bg-primary/5 inline-flex items-center gap-1"
                  >
                    {new URL(u).hostname} <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                ))}
              </div>
            </div>
          )}
          {proposal.safety_note && (
            <div className="rounded bg-rose-50 border border-rose-200 p-2 text-rose-900">
              <ShieldAlert className="inline w-3 h-3 mr-1" />
              <span className="font-semibold">Veiligheidsnotitie:</span> {proposal.safety_note}
            </div>
          )}

          {hasChanges ? (
            <div className="rounded bg-white border border-border p-2">
              <div className="font-semibold text-muted-foreground uppercase tracking-wide text-[10px] mb-1">Verschillen</div>
              {Object.entries(diff).map(([f, v]) => (
                <DiffRow key={f} field={f} current={v.current} proposed={v.proposed} />
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground italic">Geen wijzigingen in klantvelden voorgesteld.</div>
          )}

          {/* Actions — only if still pending */}
          {proposalStatus === 'pending' && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              <button
                type="button"
                disabled={busy || !hasChanges}
                onClick={() => applyScope('all')}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
                data-testid={`ec-proposal-apply-all-${product.product_id}`}
              >
                <CheckCheck className="w-3.5 h-3.5" /> Voorstel toepassen
              </button>
              <button
                type="button"
                disabled={busy || !diff?.product_url}
                onClick={() => applyScope('url')}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-border hover:bg-muted disabled:opacity-40"
                data-testid={`ec-proposal-apply-url-${product.product_id}`}
              >
                Alleen URL
              </button>
              <button
                type="button"
                disabled={busy || !diff?.price_indication}
                onClick={() => applyScope('price')}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-border hover:bg-muted disabled:opacity-40"
                data-testid={`ec-proposal-apply-price-${product.product_id}`}
              >
                Alleen prijs
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => applyScope('copy')}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-border hover:bg-muted disabled:opacity-40"
                data-testid={`ec-proposal-apply-copy-${product.product_id}`}
              >
                Alleen tekst
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => rejectAs('later')}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                data-testid={`ec-proposal-later-${product.product_id}`}
              >
                <Clock className="w-3.5 h-3.5" /> Later
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => rejectAs('rejected')}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 disabled:opacity-40"
                data-testid={`ec-proposal-reject-${product.product_id}`}
              >
                <XCircle className="w-3.5 h-3.5" /> Afwijzen
              </button>
              <button
                type="button"
                disabled={busy || checking}
                onClick={runAgentCheck}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40 ml-auto"
                data-testid={`ec-proposal-recheck-${product.product_id}`}
              >
                {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                Opnieuw controleren
              </button>
            </div>
          )}

          {proposalStatus !== 'pending' && (
            <div className="text-[10px] text-muted-foreground italic pt-1 border-t border-border/40">
              {proposalStatus === 'applied' && `Toegepast (${proposal.applied_scope}) op ${proposal.applied_at?.slice(0, 16)?.replace('T', ' ')}.`}
              {proposalStatus === 'rejected' && `Afgewezen op ${proposal.reviewed_at?.slice(0, 16)?.replace('T', ' ')}${proposal.review_reason ? ` — ${proposal.review_reason}` : ''}.`}
              {proposalStatus === 'later' && `Op later gezet ${proposal.reviewed_at?.slice(0, 16)?.replace('T', ' ')}${proposal.review_reason ? ` — ${proposal.review_reason}` : ''}.`}
              <button
                type="button"
                onClick={runAgentCheck}
                disabled={checking}
                className="ml-2 text-indigo-700 underline hover:text-indigo-900 not-italic"
              >
                {checking ? 'Controleren…' : 'Opnieuw controleren'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
